import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MenuItem, Select, TablePagination } from '@material-ui/core';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import EditOutlined from '@material-ui/icons/EditOutlined';
import Add from '@material-ui/icons/Add';
import GlobalAnnouncementService from '../../../services/GlobalAnnouncementService/GlobalAnnouncementService';
import DeleteConfirm from '../../../components/DataTable/DeleteConfirm';
import Spinner from '../../../components/Spinner/Spinner';
import { alertState } from '../../../recoil-store/recoil-state';
import { useSetRecoilState } from 'recoil';
import './GlobalAnnouncement.css';
import { formatUtcDateTime } from './globalAnnouncement.datetime';

const TYPE_OPTIONS = ['all', 'info', 'release', 'maintenance', 'security', 'feature'];
const STATUS_OPTIONS = ['all', 'active', 'inactive'];
const PUBLICATION_OPTIONS = ['all', 'test', 'live'];

function GlobalAnnouncementList() {
    const label = 'CONTENT.GLOBAL-ANNOUNCEMENT.';
    const { t } = useTranslation();
    const navigate = useNavigate();
    const setAlert = useSetRecoilState(alertState);

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [publicationFilter, setPublicationFilter] = useState('all');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        fetchData();
    }, [page, rowsPerPage]);

    const fetchData = async () => {
        setLoading(true);
        const response = await GlobalAnnouncementService.getAll(page + 1, rowsPerPage);
        if (response?.status) {
            setItems(response.data || []);
            setTotalCount(response.pagination?.totalCount || 0);
        } else {
            setItems([]);
            setTotalCount(0);
        }
        setLoading(false);
    };

    const filteredItems = useMemo(() => {
        return items.filter(item => {
            const matchesSearch = !search || [item.title, item.summary, item.content]
                .filter(Boolean)
                .some(value => value.toLowerCase().includes(search.toLowerCase()));

            const matchesType = typeFilter === 'all' || item.type === typeFilter;
            const matchesStatus = statusFilter === 'all'
                || (statusFilter === 'active' ? item.isActive : !item.isActive);
            const matchesPublication = publicationFilter === 'all'
                || (publicationFilter === 'test' ? item.isTest : !item.isTest);

            return matchesSearch && matchesType && matchesStatus && matchesPublication;
        });
    }, [items, search, typeFilter, statusFilter, publicationFilter]);

    const handleDelete = async () => {
        if (!deleteTarget) {
            return;
        }

        setDeleting(true);
        const response = await GlobalAnnouncementService.delete(deleteTarget.id);
        setDeleting(false);
        setDeleteTarget(null);

        if (response?.status) {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t(label + 'DELETE-SUCCESS'),
                severity: 'success'
            }));
            fetchData();
        } else {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t(label + 'DELETE-FAILED'),
                severity: 'error'
            }));
        }
    };

    const formatDate = (value) => formatUtcDateTime(value);

    return (
        <div className="ga-page">
            <div className="ga-page-top">
                <div className="ga-header">
                    <div className="ga-header-text">
                        <h1>{t(label + 'PAGE-TITLE')}</h1>
                        <p>{t(label + 'PAGE-DESCRIPTION')}</p>
                    </div>
                    <button className="ga-primary-btn" onClick={() => navigate('/management/global-announcement/new')}>
                        <Add style={{ verticalAlign: 'middle', marginRight: 6, fontSize: 18 }} />
                        {t(label + 'ADD')}
                    </button>
                </div>

                <div className="ga-toolbar">
                    <div className="ga-search">
                        <input
                            type="text"
                            placeholder={t(label + 'SEARCH-PLACEHOLDER')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <Select
                        className="ga-filter-select"
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        variant="outlined"
                        displayEmpty
                    >
                        {TYPE_OPTIONS.map(option => (
                            <MenuItem key={option} value={option}>
                                {option === 'all' ? t(label + 'ALL-TYPES') : t(`${label}TYPE-${option.toUpperCase()}`)}
                            </MenuItem>
                        ))}
                    </Select>
                    <Select
                        className="ga-filter-select ga-filter-select-wide"
                        value={publicationFilter}
                        onChange={(e) => setPublicationFilter(e.target.value)}
                        variant="outlined"
                        displayEmpty
                    >
                        {PUBLICATION_OPTIONS.map(option => (
                            <MenuItem key={option} value={option}>
                                {t(label + `PUBLICATION-FILTER-${option.toUpperCase()}`)}
                            </MenuItem>
                        ))}
                    </Select>
                    <Select
                        className="ga-filter-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        variant="outlined"
                        displayEmpty
                    >
                        {STATUS_OPTIONS.map(option => (
                            <MenuItem key={option} value={option}>
                                {option === 'all' ? t(label + 'ALL-STATUSES') : t(label + option.toUpperCase())}
                            </MenuItem>
                        ))}
                    </Select>
                </div>
            </div>

            <div className="ga-page-body">
                {loading ? (
                    <div className="ga-empty"><Spinner color="#002742" /></div>
                ) : filteredItems.length === 0 ? (
                    <div className="ga-empty">
                        <h3>{t(label + 'EMPTY-TITLE')}</h3>
                        <p>{t(label + 'EMPTY-DESCRIPTION')}</p>
                    </div>
                ) : (
                    <div className="ga-list">
                        {filteredItems.map(item => (
                            <article className="ga-card" key={item.id}>
                                <div className="ga-card-main">
                                    <div className="ga-card-top">
                                        <span className={`ga-badge type-${item.type}`}>
                                            {t(`${label}TYPE-${item.type.toUpperCase()}`)}
                                        </span>
                                        <span className={`ga-badge priority-${item.priority}`}>
                                            {t(`${label}PRIORITY-${item.priority.toUpperCase()}`)}
                                        </span>
                                        <span className={`ga-badge status-${item.isActive ? 'active' : 'inactive'}`}>
                                            {item.isActive ? t(label + 'ACTIVE') : t(label + 'INACTIVE')}
                                        </span>
                                        {item.isTest && (
                                            <span className="ga-badge status-test">{t(label + 'TEST-PUBLICATION-BADGE')}</span>
                                        )}
                                        {item.contentType === 'markdown' && (
                                            <span className="ga-badge type-feature">{t(label + 'MARKDOWN')}</span>
                                        )}
                                    </div>
                                    <h3 className="ga-card-title">{item.title}</h3>
                                    {item.summary && <p className="ga-card-summary">{item.summary}</p>}
                                    <div className="ga-card-meta">
                                        <span>{t(label + 'PUBLISH-START')}: {formatDate(item.publishStartAt)}</span>
                                        <span>{t(label + 'PUBLISH-END')}: {formatDate(item.publishEndAt)}</span>
                                        {item.ctaLabel && item.ctaUrl && (
                                            <span>{t(label + 'CTA')}: {item.ctaLabel}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="ga-card-actions">
                                    <button
                                        className="ga-icon-btn"
                                        onClick={() => navigate(`/management/global-announcement/${item.id}/edit`)}
                                    >
                                        <EditOutlined style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 6 }} />
                                        {t(label + 'EDIT')}
                                    </button>
                                    <button className="ga-danger-btn" onClick={() => setDeleteTarget(item)}>
                                        <DeleteOutline style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 6 }} />
                                        {t(label + 'DELETE')}
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </div>

            <div className="ga-pagination">
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage={t('CONTENT.DATATABLE.PAGINATION.ROWSPERPAGE')}
                />
            </div>

            {deleteTarget && (
                <DeleteConfirm
                    setOpenDelete={() => !deleting && setDeleteTarget(null)}
                    message={t(label + 'DELETE-MESSAGE')}
                    deleteFunc={handleDelete}
                />
            )}
        </div>
    );
}

export default GlobalAnnouncementList;
